import { useState, useCallback, useRef } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { ArrowLeft, Square, CheckCircle2, Check, X as XIcon, ChevronRight, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useRunDetail,
  useRunResults,
  useUpdateResult,
  useUpdateRunStatus,
  useHandleFailAction,
} from '../hooks/useTestRuns';
import Spinner from '../components/ui/Spinner';
import FailActualResultModal from '../components/testcase/FailActualResultModal';
import FailIssueActionModal from '../components/testcase/FailIssueActionModal';
import BugInfoModal from '../components/testcase/BugInfoModal';
import TCDetailSidePanel from '../components/testcase/TCDetailSidePanel';
import { uploadAttachments } from '../services/issueService';
import type { ProjectDetail } from '../services/projectService';
import type { TestRunResultItem } from '../services/testRunService';

// --- Constants ---

const STATUS_LABEL: Record<string, string> = {
  READY: '준비',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완료',
  ABORTED: '중단됨',
};

const STATUS_BADGE: Record<string, string> = {
  READY: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700',
  ABORTED: 'bg-red-100 text-red-700',
};

const RESULT_LABEL: Record<string, string> = {
  NOT_RUN: '미실행',
  PASS: 'PASS',
  FAIL: 'FAIL',
  BLOCKED: 'BLOCKED',
  SKIP: 'SKIP',
};

const RESULT_COLOR: Record<string, string> = {
  NOT_RUN: 'bg-gray-100 text-gray-600',
  PASS: 'bg-green-100 text-green-700',
  FAIL: 'bg-red-100 text-red-700',
  BLOCKED: 'bg-amber-100 text-amber-700',
  SKIP: 'bg-purple-100 text-purple-700',
};

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  BOUNDARY: 'Boundary',
  ALTERNATIVE: 'Alternative',
};

const TYPE_COLOR: Record<string, string> = {
  POSITIVE: 'bg-green-50 text-green-700',
  NEGATIVE: 'bg-red-50 text-red-700',
  BOUNDARY: 'bg-purple-50 text-purple-700',
  ALTERNATIVE: 'bg-blue-50 text-blue-700',
};

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

const PRIORITY_COLOR: Record<string, string> = {
  BLOCKER: 'bg-red-100 text-red-800',
  CRITICAL: 'bg-orange-100 text-orange-800',
  HIGH: 'bg-amber-100 text-amber-800',
  MEDIUM: 'bg-blue-100 text-blue-700',
  LOW: 'bg-gray-100 text-gray-600',
};

// --- Stat badge ---

function StatBadge({
  label,
  count,
  color,
  total,
}: {
  label: string;
  count: number;
  color: string;
  total: number;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${color}`}>
      <span className="text-sm font-medium">{label}</span>
      <span className="text-lg font-bold">{count}</span>
      <span className="text-sm opacity-70">({pct}%)</span>
    </div>
  );
}

// --- Main page ---

export default function TestRunExecutionPage() {
  const { specId, runId } = useParams<{ specId: string; runId: string }>();
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const navigate = useNavigate();

  // Data queries
  const { data: runDetail, isLoading: isLoadingDetail } = useRunDetail(
    project.id,
    specId!,
    runId!,
  );
  const { data: results, isLoading: isLoadingResults } = useRunResults(project.id, runId!);

  // Mutations
  const updateResultMut = useUpdateResult(project.id, specId!, runId!);
  const updateStatusMut = useUpdateRunStatus(project.id, specId!, runId!);
  const failActionMut = useHandleFailAction(project.id, specId!, runId!);

  // UI state
  const [failTarget, setFailTarget] = useState<TestRunResultItem | null>(null);
  const [issueTarget, setIssueTarget] = useState<TestRunResultItem | null>(null);
  const [bugInfoTarget, setBugInfoTarget] = useState<TestRunResultItem | null>(null);
  const [expandedTCId, setExpandedTCId] = useState<string | null>(null);
  const [sidePanelTCId, setSidePanelTCId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  // --- Handlers ---

  const handleTitleClick = useCallback((testCaseId: string) => {
    setExpandedTCId((prev) => (prev === testCaseId ? null : testCaseId));
  }, []);

  const advanceToNext = useCallback(
    (currentTestCaseId: string) => {
      if (!results) return;
      const currentIndex = results.findIndex((r) => r.testCaseId === currentTestCaseId);
      const nextIndex = currentIndex + 1;
      if (nextIndex < results.length) {
        const nextTCId = results[nextIndex].testCaseId;
        setTimeout(() => {
          setExpandedTCId(nextTCId);
          rowRefs.current[nextTCId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 250);
      } else {
        setExpandedTCId(null);
        toast('마지막 테스트 케이스입니다.', { icon: '✅' });
      }
    },
    [results],
  );

  const handleStatusChange = useCallback(
    async (result: TestRunResultItem, newStatus: string) => {
      if (newStatus === 'FAIL') {
        setFailTarget(result);
        return;
      }

      try {
        await updateResultMut.mutateAsync({
          resultId: result.resultId,
          data: { status: newStatus },
        });
        // Auto-advance: 결과 선택 후 다음 TC 아코디언 열기
        if (newStatus !== 'NOT_RUN') {
          advanceToNext(result.testCaseId);
        }
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || '상태 변경에 실패했습니다.');
      }
    },
    [updateResultMut, advanceToNext],
  );

  const handleFailConfirm = useCallback(
    async (actualResult: string) => {
      if (!failTarget) return;
      try {
        await updateResultMut.mutateAsync({
          resultId: failTarget.resultId,
          data: { status: 'FAIL', actualResult },
        });
        // FAIL 기록 후 이슈 처리 모달 열기 (auto-advance는 이슈 처리 완료 후)
        setIssueTarget(failTarget);
        setFailTarget(null);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || 'FAIL 기록에 실패했습니다.');
      }
    },
    [failTarget, updateResultMut],
  );

  // "새 작업 생성" 클릭 → BugInfoModal 열기
  const handleCreateNewIssue = useCallback(() => {
    if (!issueTarget) return;
    setBugInfoTarget(issueTarget);
    setIssueTarget(null);
  }, [issueTarget]);

  // BugInfoModal "생성" 클릭
  const handleBugInfoSubmit = useCallback(
    async (data: { priority: string; assigneeId?: string; files: File[] }) => {
      if (!bugInfoTarget) return;
      try {
        const result = await failActionMut.mutateAsync({
          resultId: bugInfoTarget.resultId,
          data: {
            action: 'CREATE_NEW',
            priority: data.priority,
            assigneeId: data.assigneeId,
          },
        });
        // 첨부파일 업로드 (이슈 생성 후)
        if (result.issue && data.files.length > 0) {
          try {
            await uploadAttachments(project.id, result.issue.id, data.files);
          } catch {
            toast.error('첨부파일 업로드에 실패했습니다.');
          }
        }
        if (result.issue) {
          toast.success(`작업 ${result.issue.issueId} 생성 완료`);
        }
        const tcId = bugInfoTarget.testCaseId;
        setBugInfoTarget(null);
        advanceToNext(tcId);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || '작업 생성에 실패했습니다.');
      }
    },
    [bugInfoTarget, failActionMut, project.id, advanceToNext],
  );

  // BugInfoModal "건너뛰기" 클릭 — 기본값으로 즉시 생성 (REQ-V44-002)
  const handleBugInfoSkip = useCallback(async () => {
    if (!bugInfoTarget) return;
    try {
      const result = await failActionMut.mutateAsync({
        resultId: bugInfoTarget.resultId,
        data: { action: 'CREATE_NEW' },
      });
      if (result.issue) {
        toast.success(`작업 ${result.issue.issueId} 생성 완료`);
      }
      const tcId = bugInfoTarget.testCaseId;
      setBugInfoTarget(null);
      advanceToNext(tcId);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '작업 생성에 실패했습니다.');
    }
  }, [bugInfoTarget, failActionMut, advanceToNext]);

  const handleReopenIssue = useCallback(
    async (issueId: string) => {
      if (!issueTarget) return;
      try {
        await failActionMut.mutateAsync({
          resultId: issueTarget.resultId,
          data: { action: 'REOPEN', issueId },
        });
        toast.success('작업 재오픈 완료');
        const tcId = issueTarget.testCaseId;
        setIssueTarget(null);
        advanceToNext(tcId);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || '작업 재오픈에 실패했습니다.');
      }
    },
    [issueTarget, failActionMut, advanceToNext],
  );

  const handleSkipIssue = useCallback(() => {
    if (!issueTarget) return;
    const tcId = issueTarget.testCaseId;
    setIssueTarget(null);
    advanceToNext(tcId);
  }, [issueTarget, advanceToNext]);

  const handleComplete = useCallback(async () => {
    if (!runDetail) return;
    if (runDetail.notRunCount > 0) {
      toast.error(
        `미실행 TC가 ${runDetail.notRunCount}건 있습니다. 모든 TC를 실행해야 완료할 수 있습니다.`,
      );
      return;
    }
    const confirmed = window.confirm('테스트를 완료하시겠습니까?');
    if (!confirmed) return;
    try {
      await updateStatusMut.mutateAsync('COMPLETED');
      toast.success('테스트가 완료되었습니다.');
      navigate(`/projects/${project.projectKey}/tests`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '테스트 완료에 실패했습니다.');
    }
  }, [runDetail, updateStatusMut, navigate, project.projectKey]);

  const handleAbort = useCallback(async () => {
    const confirmed = window.confirm(
      '테스트를 중단하시겠습니까? 현재까지의 결과는 유지됩니다.',
    );
    if (!confirmed) return;
    try {
      await updateStatusMut.mutateAsync('ABORTED');
      toast.success('테스트가 중단되었습니다.');
      navigate(`/projects/${project.projectKey}/tests`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '테스트 중단에 실패했습니다.');
    }
  }, [updateStatusMut, navigate, project.projectKey]);

  // --- Loading / Error ---

  if (isLoadingDetail || isLoadingResults) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (!runDetail || !results) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-red-500">테스트 실행 데이터를 불러올 수 없습니다.</p>
        <button
          onClick={() => navigate(`/projects/${project.projectKey}/tests`)}
          className="text-sm text-primary-600 hover:underline"
        >
          TC 목록으로 돌아가기
        </button>
      </div>
    );
  }

  const isActive = runDetail.status === 'READY' || runDetail.status === 'IN_PROGRESS';
  const progressPct =
    runDetail.totalCount > 0
      ? Math.round(
          ((runDetail.totalCount - runDetail.notRunCount) / runDetail.totalCount) * 100,
        )
      : 0;

  return (
    <div className="space-y-5">
      {/* 1-1. 상단 헤더 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{runDetail.title}</h2>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${STATUS_BADGE[runDetail.status]}`}
            >
              {STATUS_LABEL[runDetail.status]}
            </span>
          </div>
          {runDetail.dashboard.previousComparison && (
            <div className="text-sm text-gray-500">
              vs {runDetail.dashboard.previousComparison.previousRunNumber}차:{' '}
              <span
                className={
                  runDetail.dashboard.previousComparison.passDiff >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }
              >
                PASS {runDetail.dashboard.previousComparison.passDiff >= 0 ? '+' : ''}
                {runDetail.dashboard.previousComparison.passDiff}
              </span>
              {' / '}
              <span
                className={
                  runDetail.dashboard.previousComparison.failDiff <= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }
              >
                FAIL {runDetail.dashboard.previousComparison.failDiff >= 0 ? '+' : ''}
                {runDetail.dashboard.previousComparison.failDiff}
              </span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">진행률</span>
            <span className="font-medium text-gray-900">
              {progressPct}% ({runDetail.totalCount - runDetail.notRunCount}/{runDetail.totalCount})
            </span>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* 1-2. 실행 현황 바 */}
      <div className="grid grid-cols-5 gap-3">
        <StatBadge
          label="PASS"
          count={runDetail.passCount}
          color="bg-green-50 text-green-700"
          total={runDetail.totalCount}
        />
        <StatBadge
          label="FAIL"
          count={runDetail.failCount}
          color="bg-red-50 text-red-700"
          total={runDetail.totalCount}
        />
        <StatBadge
          label="BLOCKED"
          count={runDetail.blockedCount}
          color="bg-amber-50 text-amber-700"
          total={runDetail.totalCount}
        />
        <StatBadge
          label="SKIP"
          count={runDetail.skipCount}
          color="bg-purple-50 text-purple-700"
          total={runDetail.totalCount}
        />
        <StatBadge
          label="미실행"
          count={runDetail.notRunCount}
          color="bg-gray-50 text-gray-600"
          total={runDetail.totalCount}
        />
      </div>

      {/* 1-3. TC 결과 테이블 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-28">
                  TC ID
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  제목
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  유형
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-20">
                  우선순위
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  이전 결과
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  상태
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-72">
                  액션
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    결과 데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                results.map((r) => (
                  <ResultRowWithAccordion
                    key={r.resultId}
                    result={r}
                    isActive={isActive}
                    isPending={updateResultMut.isPending}
                    isExpanded={expandedTCId === r.testCaseId}
                    onStatusChange={handleStatusChange}
                    onTitleClick={handleTitleClick}
                    onTcIdClick={(tcId) => setSidePanelTCId(tcId)}
                    rowRef={(el) => { rowRefs.current[r.testCaseId] = el; }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 1-5. 하단 버튼 */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/projects/${project.projectKey}/tests`)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="size-4" />
          TC 목록으로
        </button>
        {isActive && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleAbort}
              disabled={updateStatusMut.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <Square className="size-4" />
              테스트 중단
            </button>
            <button
              onClick={handleComplete}
              disabled={updateStatusMut.isPending || runDetail.notRunCount > 0}
              title={
                runDetail.notRunCount > 0
                  ? `미실행 TC ${runDetail.notRunCount}건`
                  : undefined
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CheckCircle2 className="size-4" />
              테스트 완료
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {failTarget && (
        <FailActualResultModal
          tcId={failTarget.testCase.tcId}
          tcTitle={failTarget.testCase.title}
          onConfirm={handleFailConfirm}
          onCancel={() => setFailTarget(null)}
          isPending={updateResultMut.isPending}
        />
      )}

      {issueTarget && (
        <FailIssueActionModal
          tcId={issueTarget.testCase.tcId}
          tcTitle={issueTarget.testCase.title}
          linkedIssues={issueTarget.testCase.linkedIssues ?? []}
          onCreateNew={handleCreateNewIssue}
          onReopen={handleReopenIssue}
          onSkip={handleSkipIssue}
          isPending={failActionMut.isPending}
        />
      )}

      {bugInfoTarget && (
        <BugInfoModal
          bugTitle={`[${bugInfoTarget.testCase.tcId}] ${bugInfoTarget.testCase.title} - FAIL`}
          members={project.members ?? []}
          onSubmit={handleBugInfoSubmit}
          onSkip={handleBugInfoSkip}
          isPending={failActionMut.isPending}
        />
      )}

      {/* TC Detail Side Panel */}
      {sidePanelTCId && (
        <TCDetailSidePanel
          projectId={project.id}
          testCaseId={sidePanelTCId}
          runId={runId}
          currentResultStatus={results?.find((r) => r.testCaseId === sidePanelTCId)?.status}
          onClose={() => setSidePanelTCId(null)}
        />
      )}
    </div>
  );
}

// --- Row + Accordion wrapper ---

function ResultRowWithAccordion({
  result: r,
  isActive,
  isPending,
  isExpanded,
  onStatusChange,
  onTitleClick,
  onTcIdClick,
  rowRef,
}: {
  result: TestRunResultItem;
  isActive: boolean;
  isPending: boolean;
  isExpanded: boolean;
  onStatusChange: (result: TestRunResultItem, status: string) => void;
  onTitleClick: (testCaseId: string) => void;
  onTcIdClick: (testCaseId: string) => void;
  rowRef: (el: HTMLTableRowElement | null) => void;
}) {
  return (
    <>
      <tr ref={rowRef} className="hover:bg-gray-50 transition-colors">
        {/* TC ID — clickable to open side panel */}
        <td className="px-4 py-3 whitespace-nowrap">
          <button
            onClick={() => onTcIdClick(r.testCaseId)}
            className="font-mono text-sm text-primary-600 hover:text-primary-800 hover:underline cursor-pointer transition-colors"
          >
            {r.testCase.tcId}
          </button>
        </td>

        {/* Title (clickable with chevron) */}
        <td
          className="px-4 py-3 text-gray-900 cursor-pointer select-none group"
          onClick={() => onTitleClick(r.testCaseId)}
        >
          <span className="inline-flex items-center gap-1 group-hover:text-primary-600 group-hover:underline transition-colors">
            {isExpanded ? (
              <ChevronDown className="size-4 shrink-0 text-gray-400" />
            ) : (
              <ChevronRight className="size-4 shrink-0 text-gray-400" />
            )}
            {r.testCase.title}
          </span>
        </td>

        {/* Type */}
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${TYPE_COLOR[r.testCase.type] || 'bg-gray-100 text-gray-600'}`}
          >
            {TYPE_LABEL[r.testCase.type] || r.testCase.type}
          </span>
        </td>

        {/* Priority */}
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${PRIORITY_COLOR[r.testCase.priority] || 'bg-gray-100 text-gray-600'}`}
          >
            {PRIORITY_LABEL[r.testCase.priority] || r.testCase.priority}
          </span>
        </td>

        {/* Previous result */}
        <td className="px-4 py-3 text-center">
          {r.previousStatus ? (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${RESULT_COLOR[r.previousStatus] || 'bg-gray-100 text-gray-600'}`}
            >
              {RESULT_LABEL[r.previousStatus] || r.previousStatus}
            </span>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </td>

        {/* Current status */}
        <td className="px-4 py-3 text-center">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${RESULT_COLOR[r.status] || 'bg-gray-100 text-gray-600'}`}
          >
            {RESULT_LABEL[r.status] || r.status}
          </span>
        </td>

        {/* Action buttons */}
        <td className="px-4 py-3 text-center">
          {isActive ? (
            <div className="inline-flex items-center gap-1">
              <button
                onClick={() => onStatusChange(r, 'PASS')}
                disabled={r.status === 'PASS' || isPending}
                className={`px-2.5 py-1 text-sm font-medium rounded transition-colors ${
                  r.status === 'PASS'
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50'
                }`}
              >
                <Check className="size-3.5 inline -mt-0.5 mr-0.5" />
                PASS
              </button>
              <button
                onClick={() => onStatusChange(r, 'FAIL')}
                disabled={r.status === 'FAIL' || isPending}
                className={`px-2.5 py-1 text-sm font-medium rounded transition-colors ${
                  r.status === 'FAIL'
                    ? 'bg-red-600 text-white cursor-default'
                    : 'bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50'
                }`}
              >
                <XIcon className="size-3.5 inline -mt-0.5 mr-0.5" />
                FAIL
              </button>
              <button
                onClick={() => onStatusChange(r, 'BLOCKED')}
                disabled={r.status === 'BLOCKED' || isPending}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                  r.status === 'BLOCKED'
                    ? 'bg-amber-600 text-white cursor-default'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50'
                }`}
              >
                BLOCKED
              </button>
              <button
                onClick={() => onStatusChange(r, 'SKIP')}
                disabled={r.status === 'SKIP' || isPending}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                  r.status === 'SKIP'
                    ? 'bg-purple-600 text-white cursor-default'
                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50'
                }`}
              >
                SKIP
              </button>
              {r.status !== 'NOT_RUN' && (
                <button
                  onClick={() => onStatusChange(r, 'NOT_RUN')}
                  disabled={isPending}
                  className="px-2 py-1 text-sm font-medium text-gray-500 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                  title="초기화"
                >
                  리셋
                </button>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </td>
      </tr>

      {/* Accordion row */}
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-gray-50 border-t border-gray-200 px-4 py-4 pl-12">
            <TCAccordionContent testCase={r.testCase} />
          </td>
        </tr>
      )}
    </>
  );
}

// --- Accordion content ---

function TCAccordionContent({
  testCase,
}: {
  testCase: TestRunResultItem['testCase'];
}) {
  const steps = testCase.steps as Array<{ step: number; action: string }> | null;

  return (
    <div className="space-y-3">
      {/* 사전조건 */}
      <div>
        <span className="text-sm text-gray-500 font-semibold">사전조건</span>
        {testCase.precondition ? (
          <p className="text-sm mt-1">{testCase.precondition}</p>
        ) : (
          <p className="text-sm text-gray-400 italic mt-1">없음</p>
        )}
      </div>

      {/* 테스트 단계 */}
      <div>
        <span className="text-sm text-gray-500 font-semibold">테스트 단계</span>
        {!steps || steps.length === 0 ? (
          <p className="text-sm text-gray-400 italic mt-1">등록된 테스트 단계가 없습니다.</p>
        ) : (
          <ol className="list-decimal list-inside text-sm mt-1 space-y-1">
            {steps.map((s) => (
              <li key={s.step}>{s.action}</li>
            ))}
          </ol>
        )}
      </div>

      {/* 기대결과 */}
      <div>
        <span className="text-sm text-gray-500 font-semibold">기대결과</span>
        {testCase.expectedResult ? (
          <p className="text-sm mt-1">{testCase.expectedResult}</p>
        ) : (
          <p className="text-sm text-gray-400 italic mt-1">없음</p>
        )}
      </div>
    </div>
  );
}
